import {
 ref, reactive, computed, watch,
} from '@vue/composition-api'
import { utils, constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { formatNumber, textTruncate } from '@core/utils/utils'
import {
  dataToCurrency,
  getDateFromTimestamp,
  filterDataByDate,
} from '@/common-utils'
import constants, {
  EXPORT_ORDERS_OPTIONS, FILE_FORMATS, TABS, ORDER_STATUS_LABELS, ORDERS_TEXT_BRAND, ORDERS_TEXT_RETAILER,
} from '@/constants'
import store from '@/store'
import { GET_ORDER_LIST, GET_ORDER_FILTER_OPTIONS, ORDER_PRODUCTION_FILE_EXPORT } from '@/store/modules/order.module'
import { kpEndpoint } from '@/axios'
import { DOWNLOAD_FILE } from '@/store/modules/common.module'
import { GET_ORDER_TABLE_COLUMNS, salesRepField } from '@/table-columns-constants'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { SHOW_DOWNLOADS_NAV_INDICATOR } from '@/store/modules/notification.module'
import { getSearchByEndpoint, ORDER_SEARCH_FIELD } from '@/views/apps/order/order-utils'

const { formatCurrency, getCurrentUTCDateAndTime, getMonthAndYearFromDate } = utils

const {
  ORDER_STATUS,
  KP_DATE_FORMAT,
} = c

export function useOrderList() {
  // Reactive state
  const orders = ref([])
  const totalOrders = ref(0)
  const isLoaded = ref(false)
  const tableColumns = ref([])
  const filterDate = ref(null)
  const expectedDate = ref(null)
  const currentTab = ref(TABS.ALL)
  const entityNames = ref([])
  const collectionNames = ref([])
  const isExportingOrders = ref(false)
  const queryParams = reactive({
    page: 1,
    limit: 10,
  })
  // KP-3336: Vue 2 cannot track property *addition* on a reactive object, so a
  // computed that reads dynamically-added queryParams keys (collectionName,
  // status, …) won't re-run when a filter is first applied. Every filter
  // mutation is immediately followed by loadOrders(), which bumps this tick;
  // the filter computeds depend on it and thus re-evaluate against the current
  // queryParams whenever a filter changes.
  const filtersTick = ref(0)

  // Store-derived computeds
  const profile = computed(() => store.state.auth.profileData)
  const salesReps = computed(() => store.state.auth.salesReps)
  const salesManagers = computed(() => store.state.auth.salesManagers)
  const isBrand = computed(() => store.getters.isBrand)
  const isRetailer = computed(() => store.getters.isRetailer)
  const isEntityAdmin = computed(() => store.getters.isEntityAdmin)
  const isSalesManager = computed(() => store.getters.isSalesManager)
  const role = computed(() => store.getters.role)

  // Page computeds
  const hasPagination = computed(() => !!orders.value.length)

  const isFilterApplied = computed(() => {
    filtersTick.value // eslint-disable-line no-unused-expressions
    return Object.keys(queryParams).length > 2
  })

  // KP-3336: pagination + the active tab (isSelfManagedOrder) are not "filters",
  // so they don't count towards the Clear-filters affordance.
  const FILTER_CLEAR_IGNORED_KEYS = ['page', 'limit', 'isSelfManagedOrder']

  // True when any filter-row selection (collection / retailer / status / dates /
  // reps) is active — drives the "Clear filters" button visibility. Reads
  // filtersTick so it re-evaluates when filters change (Vue 2 can't track the
  // addition of new queryParams keys on its own).
  const hasActiveFilters = computed(() => {
    filtersTick.value // eslint-disable-line no-unused-expressions
    return Object.entries(queryParams).some(
      ([key, value]) => !FILTER_CLEAR_IGNORED_KEYS.includes(key)
        && value !== '' && value !== undefined && value !== null,
    )
  })

  // Reset every filter-row selection while keeping the page size and the active
  // tab, then reload from the first page.
  function clearFilters() {
    const { limit, isSelfManagedOrder } = queryParams
    Object.keys(queryParams).forEach(key => delete queryParams[key])
    queryParams.page = 1
    queryParams.limit = limit
    if (isSelfManagedOrder !== undefined) {
      queryParams.isSelfManagedOrder = isSelfManagedOrder
    }
    filterDate.value = null
    expectedDate.value = null
    loadOrders()
  }

  const getOrderStatus = computed(() => {
    if (isBrand.value) {
      return Object.keys(ORDER_STATUS_LABELS).filter(status => status !== ORDER_STATUS.NEW)
    }
    return Object.keys(ORDER_STATUS_LABELS)
  })

  // Methods
  function setTableColumns() {
    const columns = GET_ORDER_TABLE_COLUMNS()
    const hasElevatedRole = isSalesManager.value || isEntityAdmin.value
    const shouldShowSalesRepColumn = hasElevatedRole && isBrand.value
    if (shouldShowSalesRepColumn) {
      columns.unshift(salesRepField)
    }
    tableColumns.value = columns
  }

  function currency(data) {
    if (isBrand.value) {
      return data.currencyForBrand
    }
    return data.currency
  }

  function onSelectedReps(selectedReps = []) {
    queryParams.salesRepIds = selectedReps.map(r => r.userId).join(',')
    loadOrders()
  }

  function loadEntityNames(searchText = '') {
    const params = {
      search: searchText,
    }
    store.dispatch(GET_ORDER_FILTER_OPTIONS, { searchField: getSearchByEndpoint(isBrand.value), params }).then(res => {
      entityNames.value = res?.data?.data?.values || []
    })
  }

  function loadCollectionNames(searchText = '') {
    const params = {
      search: searchText,
    }
    store.dispatch(GET_ORDER_FILTER_OPTIONS, { searchField: ORDER_SEARCH_FIELD.COLLECTION_NAME, params }).then(res => {
      collectionNames.value = res?.data?.data?.values || []
    })
  }

  function onSelectStatus(status = '') {
    if (queryParams.status === status) {
      delete queryParams.status
    } else {
      queryParams.status = status
    }
    loadOrders()
  }

  function getFormattedTimeFromDate(timestamp) {
    const utcDate = new Date(timestamp)
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimeZone,
    }
    return new Intl.DateTimeFormat(undefined, options).format(utcDate)
  }

  function onSelectEntityName(name = '') {
    if (queryParams.entityName === name) {
      delete queryParams.entityName
    } else {
      queryParams.entityName = name
    }
    loadOrders()
  }

  function onSelectCollectionName(name = '') {
    if (queryParams.collectionName === name) {
      delete queryParams.collectionName
    } else {
      queryParams.collectionName = name
    }
    loadOrders()
  }

  function onLoadTab(tab = '') {
    if (tab) {
      currentTab.value = tab
    }
    switch (currentTab.value) {
      case TABS.ALL: {
        if (queryParams.hasOwnProperty('isSelfManagedOrder')) {
          delete queryParams.isSelfManagedOrder
        }
        break
      }
      case TABS.MARKETPLACE: {
        queryParams.isSelfManagedOrder = false
        break
      }
      case TABS.DIRECT: {
        queryParams.isSelfManagedOrder = true
        break
      }
      default: {
        break
      }
    }
    loadOrders()
  }

  function onSelectOrderDate(dates) {
    if (dates.length) {
      if (dates.length === 2) {
        queryParams.startDate = dates[0]
        queryParams.endDate = new Date(dates[1]).setDate(
          new Date(dates[1]).getDate() + 1,
        )
        loadOrders()
      }
    } else {
      delete queryParams.startDate
      delete queryParams.endDate
      loadOrders()
    }
  }

  function onSelectExpectedDate(dates) {
    if (dates.length) {
      if (dates.length === 2) {
        queryParams.minExpectedAvailabilityDate = dates[0]
        queryParams.maxExpectedAvailabilityDate = dates[1]
        loadOrders()
      }
    } else {
      delete queryParams.minExpectedAvailabilityDate
      delete queryParams.maxExpectedAvailabilityDate
      loadOrders()
    }
  }

  function getStatusText(status) {
    if (isRetailer.value) {
      return ORDERS_TEXT_RETAILER[status] || status
    }
    return ORDERS_TEXT_BRAND[status] || status
  }

  function loadOrders() {
    // KP-3336: synchronously signal the filter computeds that queryParams may
    // have changed (covers keys Vue 2 can't reactively track on addition).
    filtersTick.value += 1
    const params = {
      ...queryParams,
      page: queryParams.page - 1,
    }
    return store.dispatch(GET_ORDER_LIST, params)
      .then(res => {
        orders.value = res.data.data.orders
        totalOrders.value = res.data.data.count
        isLoaded.value = true
      })
  }

  function getOrderStatusCssClass(status) {
    if (status === ORDER_STATUS.CANCELLED) return 'cancelled'
    if (status === ORDER_STATUS.REJECTED) return 'rejected'
    if (status === ORDER_STATUS.CONFIRMED) return 'confirmed'
    if (status === constants.ORDER_STATUS.MODIFIED) return 'modified'
    if (status === constants.ORDER_STATUS.PARTIALLY_CONFIRMED) return 'partially-confirmed'
    return 'pending'
  }

  function shouldUseBrandProductionFlow(optionValue) {
    if (!isBrand.value) {
      return false
    }
    return [
      EXPORT_ORDERS_OPTIONS.PRODUCTION_FILE.value,
      EXPORT_ORDERS_OPTIONS.PRODUCTION_FILE_WITH_IMAGES.value,
    ].includes(optionValue)
  }

  function getExportParams(optionValue) {
    const { page, limit, ...params } = queryParams

    if (optionValue === EXPORT_ORDERS_OPTIONS.PRODUCTION_FILE.value) {
      params.isDetailed = true
    }

    if (optionValue === EXPORT_ORDERS_OPTIONS.PRODUCTION_FILE_WITH_IMAGES.value) {
      params.includeImages = true
    }

    return params
  }

  function getExportFileName(optionValue, formattedDate) {
    if (optionValue === EXPORT_ORDERS_OPTIONS.PRODUCTION_FILE.value) {
      return `Production File ${formattedDate}.xlsx`
    }

    return `Order Export ${formattedDate}.xlsx`
  }

  async function exportOrdersProductionFile(params) {
    return store.dispatch(ORDER_PRODUCTION_FILE_EXPORT, params)
      .then(res => {
        apiToastSuccess(res.data.message)
        store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
        isExportingOrders.value = false
      })
      .catch(err => {
        apiToastError(err)
        isExportingOrders.value = false
      })
  }

  async function onExportOrders(option = {}) {
    isExportingOrders.value = true
    const formattedDate = getCurrentUTCDateAndTime()

    const optionValue = option?.value
    const exportParams = getExportParams(optionValue)

    if (shouldUseBrandProductionFlow(optionValue)) {
      try {
        await exportOrdersProductionFile(exportParams)
      } finally {
        isExportingOrders.value = false
      }
      return { success: true, usedProductionFlow: true }
    }

    const payload = {
      url: kpEndpoint.order.download,
      fileName: getExportFileName(optionValue, formattedDate),
      fileFormat: FILE_FORMATS.XLSX,
      params: exportParams,
    }

    return store
      .dispatch(DOWNLOAD_FILE, payload)
      .then(() => {
        isExportingOrders.value = false
        return { success: true, usedProductionFlow: false }
      })
      .catch(err => {
        apiToastError(err)
        isExportingOrders.value = false
        return { success: false }
      })
  }

  function initOrderList(orderList = []) {
    setTableColumns()
    if (orderList.length !== 0) {
      orders.value = orderList
      isLoaded.value = true
    } else {
      onLoadTab()
      loadEntityNames()
      loadCollectionNames()
    }
  }

  // Watch role changes
  watch(role, () => {
    setTableColumns()
  })

  return {
    // State
    orders,
    totalOrders,
    isLoaded,
    tableColumns,
    filterDate,
    expectedDate,
    currentTab,
    entityNames,
    collectionNames,
    isExportingOrders,
    queryParams,

    // Computeds
    profile,
    salesReps,
    salesManagers,
    isBrand,
    isRetailer,
    isEntityAdmin,
    isSalesManager,
    hasPagination,
    isFilterApplied,
    hasActiveFilters,
    clearFilters,
    getOrderStatus,

    // Methods
    setTableColumns,
    currency,
    onSelectedReps,
    loadEntityNames,
    loadCollectionNames,
    onSelectStatus,
    getFormattedTimeFromDate,
    onSelectEntityName,
    onSelectCollectionName,
    onLoadTab,
    onSelectOrderDate,
    onSelectExpectedDate,
    getStatusText,
    loadOrders,
    getOrderStatusCssClass,
    shouldUseBrandProductionFlow,
    getExportParams,
    getExportFileName,
    exportOrdersProductionFile,
    onExportOrders,
    initOrderList,

    // Constants & utilities
    TABS,
    ORDER_STATUS_LABELS,
    ORDERS_TEXT_BRAND,
    ORDERS_TEXT_RETAILER,
    EXPORT_ORDERS_OPTIONS,
    KP_DATE_FORMAT,
    textTruncate,
    dataToCurrency,
    getDateFromTimestamp,
    filterDataByDate,
    formatNumber,
    formatCurrency,
    getMonthAndYearFromDate,
  }
}
